import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';

import {
	CadRendererInteractionStateEvent,
	CadRendererSnapshotEvent,
	CadRendererSyncNamespace,
} from '../broadcast-manager.js';
import { useSyncSnapshotHydration, useSyncSnapshotResponder } from '../sync-core.js';
import { compareVersion, pickLatest } from '../sync-protocol.js';

import type {
	DrawingProperties,
	DrawingPropertyValue,
	ElementSelectionArgs,
	InteractionState,
} from '@cadstrom/renderer';
import { DrawingProperties as DrawingPropertiesValue } from '@cadstrom/renderer';
import type { Utils } from '@cadstrom/sigma-engine-shared';
import type { CadRendererSyncCore } from '../sync-core.js';
import type { VersionedSnapshot } from '../sync-protocol.js';
import type { ValueRef } from '../sync-types.js';

export const HIGHLIGHT_SYNC_DEBOUNCE_MS = 50;

const DrawingPropertiesSchema = z.nativeEnum(DrawingPropertiesValue) satisfies Utils.Zot<DrawingProperties>;

const DrawingPropertyValueSchema = z.union([
	z.string(),
	z.number(),
	z.boolean(),
]) satisfies Utils.Zot<DrawingPropertyValue>;

export type SerializableInteractionMap = readonly (readonly [DrawingProperties, readonly DrawingPropertyValue[]])[];

const SerializableInteractionMapSchema = z.array(
	z.tuple([DrawingPropertiesSchema, z.array(DrawingPropertyValueSchema)]),
) satisfies Utils.Zot<SerializableInteractionMap>;

export type InteractionSnapshotPayload = {
	requestId?: string;
	selection: SerializableInteractionMap;
	highlight: SerializableInteractionMap;
	updatedAt: number;
	sourceWindowId: string;
};

const InteractionSnapshotPayloadSchema = z.object({
	requestId: z.string().optional(),
	selection: SerializableInteractionMapSchema,
	highlight: SerializableInteractionMapSchema,
	updatedAt: z.number(),
	sourceWindowId: z.string(),
}) satisfies Utils.Zot<InteractionSnapshotPayload>;

/**
 * Bridges renderer selection and highlight state to the sync channel.
 *
 * Local interaction events publish serialized snapshots, remote snapshots update the interaction
 * manager when newer, and initial hydration asks linked peers for their latest interaction state.
 */
export function useInteractionStateBridge({
	core,
	interactionManager,
	suppressHistoryForHydrationRef,
}: {
	core: CadRendererSyncCore;
	interactionManager: InteractionState;
	suppressHistoryForHydrationRef: ValueRef<() => void>;
}) {
	const applyingRemoteRef = useRef(false);
	const latestVersionRef = useRef<VersionedSnapshot>({ updatedAt: 0, sourceWindowId: 'local' });
	const highlightPublishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingHighlightEventRef = useRef<CadRendererInteractionStateEvent | null>(null);

	const cancelPendingHighlightSnapshot = useCallback(() => {
		if (highlightPublishTimerRef.current !== null) {
			clearTimeout(highlightPublishTimerRef.current);
			highlightPublishTimerRef.current = null;
		}
		pendingHighlightEventRef.current = null;
	}, []);

	const createSnapshot = useCallback(
		(bumpVersion: boolean): InteractionSnapshotPayload => {
			if (bumpVersion) {
				latestVersionRef.current = {
					// Keep local interaction publishes strictly ordered even when history replay
					// emits multiple selection events in the same millisecond.
					updatedAt: Math.max(Date.now(), latestVersionRef.current.updatedAt + 1),
					sourceWindowId: core.windowId,
				};
			}
			const { updatedAt, sourceWindowId } = latestVersionRef.current;
			return {
				selection: serializeInteractionMap(interactionManager.getCurrentSelection()),
				highlight: serializeInteractionMap(interactionManager.getCurrentHighlight()),
				updatedAt,
				sourceWindowId,
			};
		},
		[core.windowId, interactionManager],
	);

	const applySnapshot = useCallback(
		(payload: InteractionSnapshotPayload, isHydration: boolean) => {
			if (compareVersion(payload, latestVersionRef.current) <= 0) {
				return;
			}

			if (isHydration) {
				suppressHistoryForHydrationRef.current();
			}

			cancelPendingHighlightSnapshot();
			applyingRemoteRef.current = true;
			try {
				applyInteractionMap(
					interactionManager.getCurrentSelection(),
					payload.selection,
					() => interactionManager.clearSelection(),
					(property, value, add) => interactionManager.selectElementsByProperty(property, value, add),
				);
				applyInteractionMap(
					interactionManager.getCurrentHighlight(),
					payload.highlight,
					() => interactionManager.clearHighlight(),
					(property, value, add) => interactionManager.highlightElementsByProperty(property, value, add),
				);
				latestVersionRef.current = { updatedAt: payload.updatedAt, sourceWindowId: payload.sourceWindowId };
			} finally {
				applyingRemoteRef.current = false;
			}
		},
		[cancelPendingHighlightSnapshot, interactionManager, suppressHistoryForHydrationRef],
	);

	useEffect(() => {
		const publishSnapshot = (eventName: CadRendererInteractionStateEvent) => {
			if (applyingRemoteRef.current) {
				return;
			}
			core.publish(CadRendererSyncNamespace.InteractionState, eventName, createSnapshot(true));
		};

		const publishImmediateSnapshot = (eventName: CadRendererInteractionStateEvent) => {
			cancelPendingHighlightSnapshot();
			publishSnapshot(eventName);
		};

		const publishDebouncedHighlightSnapshot = (eventName: CadRendererInteractionStateEvent) => {
			if (applyingRemoteRef.current) {
				return;
			}
			pendingHighlightEventRef.current = eventName;
			if (highlightPublishTimerRef.current !== null) {
				clearTimeout(highlightPublishTimerRef.current);
			}
			highlightPublishTimerRef.current = setTimeout(() => {
				const pendingEventName = pendingHighlightEventRef.current ?? eventName;
				highlightPublishTimerRef.current = null;
				pendingHighlightEventRef.current = null;
				publishSnapshot(pendingEventName);
			}, HIGHLIGHT_SYNC_DEBOUNCE_MS);
		};

		const publishSelection = () => publishImmediateSnapshot(CadRendererInteractionStateEvent.SelectElementsByProperty);
		const publishClearSelection = () => publishImmediateSnapshot(CadRendererInteractionStateEvent.ClearSelection);
		const publishHighlight = () =>
			publishDebouncedHighlightSnapshot(CadRendererInteractionStateEvent.HighlightElementsByProperty);
		const publishClearHighlight = () =>
			publishDebouncedHighlightSnapshot(CadRendererInteractionStateEvent.ClearHighlight);

		interactionManager.on(CadRendererInteractionStateEvent.SelectElementsByProperty, publishSelection);
		interactionManager.on(CadRendererInteractionStateEvent.ClearSelection, publishClearSelection);
		interactionManager.on(CadRendererInteractionStateEvent.HighlightElementsByProperty, publishHighlight);
		interactionManager.on(CadRendererInteractionStateEvent.ClearHighlight, publishClearHighlight);

		return () => {
			interactionManager.removeListener(CadRendererInteractionStateEvent.SelectElementsByProperty, publishSelection);
			interactionManager.removeListener(CadRendererInteractionStateEvent.ClearSelection, publishClearSelection);
			interactionManager.removeListener(CadRendererInteractionStateEvent.HighlightElementsByProperty, publishHighlight);
			interactionManager.removeListener(CadRendererInteractionStateEvent.ClearHighlight, publishClearHighlight);
			cancelPendingHighlightSnapshot();
		};
	}, [cancelPendingHighlightSnapshot, core, createSnapshot, interactionManager]);

	const createResponseSnapshot = useCallback(() => createSnapshot(false), [createSnapshot]);

	useSyncSnapshotResponder({
		core,
		namespace: CadRendererSyncNamespace.InteractionState,
		createPayload: createResponseSnapshot,
	});

	useEffect(() => {
		return core.subscribe(CadRendererSyncNamespace.InteractionState, (envelope) => {
			if (
				envelope.event === CadRendererSnapshotEvent.SnapshotRequest ||
				envelope.event === CadRendererSnapshotEvent.SnapshotResponse
			) {
				return;
			}
			const payload = parseInteractionSnapshot(envelope.payload);
			if (payload) {
				applySnapshot(payload, false);
			}
		});
	}, [applySnapshot, core]);

	const applyHydrationResponses = useCallback(
		(responses: readonly InteractionSnapshotPayload[]) => {
			const latest = pickLatest(responses);
			if (latest) {
				applySnapshot(latest, true);
			}
		},
		[applySnapshot],
	);

	useSyncSnapshotHydration({
		core,
		namespace: CadRendererSyncNamespace.InteractionState,
		parsePayload: parseInteractionSnapshot,
		applyResponses: applyHydrationResponses,
	});
}

export function serializeInteractionMap(
	value: ElementSelectionArgs['allSelectionArgs'] | undefined,
): SerializableInteractionMap {
	if (!value || value.size === 0) {
		return [];
	}

	return [...value.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([property, values]) => [property, [...values].sort(sortDrawingPropertyValues)] as const);
}

export function deserializeInteractionMap(
	value: SerializableInteractionMap | undefined,
): ElementSelectionArgs['allSelectionArgs'] {
	if (!value || value.length === 0) {
		return new Map<DrawingProperties, Set<DrawingPropertyValue>>();
	}

	return new Map(value.map(([property, values]) => [property, new Set(values)]));
}

function parseInteractionSnapshot(value: unknown): InteractionSnapshotPayload | null {
	const result = InteractionSnapshotPayloadSchema.safeParse(value);
	return result.success ? result.data : null;
}

function sortDrawingPropertyValues(a: DrawingPropertyValue, b: DrawingPropertyValue): number {
	return String(a).localeCompare(String(b));
}

function applyInteractionMap(
	current: ElementSelectionArgs['allSelectionArgs'] | undefined,
	next: SerializableInteractionMap,
	clear: () => void,
	setByProperty: (property: DrawingProperties, value: DrawingPropertyValue, add: boolean) => void,
): void {
	if (interactionMapsEqual(serializeInteractionMap(current), next)) {
		return;
	}

	clear();
	const nextMap = deserializeInteractionMap(next);
	let index = 0;
	for (const [property, values] of nextMap.entries()) {
		for (const value of values) {
			setByProperty(property, value, index > 0);
			index += 1;
		}
	}
}

function interactionMapsEqual(a: SerializableInteractionMap, b: SerializableInteractionMap): boolean {
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i += 1) {
		const [aProperty, aValues] = a[i]!;
		const [bProperty, bValues] = b[i]!;
		if (aProperty !== bProperty || aValues.length !== bValues.length) {
			return false;
		}
		for (let j = 0; j < aValues.length; j += 1) {
			if (aValues[j] !== bValues[j]) {
				return false;
			}
		}
	}
	return true;
}
